"use client";

import { useEffect } from "react";
import { COOKIE_CONSENT_KEY, type ConsentValue } from "@/components/CookieConsent";

/**
 * Consent Mode v2 — client-side consent handler for GTM.
 *
 * Since auto-consent is enabled (all cookies granted by default), this
 * component only needs to handle the opt-out case: when a user opens
 * Cookie Settings and turns off advertising cookies.
 *
 * GTM (and all tags it manages — GA4, Google Ads, Facebook Pixel, etc.)
 * automatically respect the consent state.
 */
export default function ConsentModeDefaults() {
  useEffect(() => {
    // Check stored preference and apply if the user previously opted out
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY) as ConsentValue | null;
    if (stored === "essential") {
      revokeAdConsent();
    }

    const onConsent = (e: Event) => {
      const detail = (e as CustomEvent<{ value: ConsentValue }>).detail;
      if (detail?.value === "essential") {
        revokeAdConsent();
      } else if (detail?.value === "all") {
        grantAllConsent();
      }
    };

    window.addEventListener("mc:cookie-consent", onConsent);
    return () => window.removeEventListener("mc:cookie-consent", onConsent);
  }, []);

  // Sync across tabs
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === COOKIE_CONSENT_KEY) {
        if (e.newValue === "essential") {
          revokeAdConsent();
        } else if (e.newValue === "all") {
          grantAllConsent();
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return null;
}

/** Revoke ad-related consent — user opted out of advertising cookies. */
function revokeAdConsent() {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  // Must use `arguments` (not rest params) — GTM expects an Arguments object, not an array.
  // eslint-disable-next-line prefer-rest-params
  function gtag() { window.dataLayer!.push(arguments); }
  // @ts-expect-error — gtag is untyped; called the same way as the inline <head> snippet
  gtag("consent", "update", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
}

/** Re-grant all consent — user re-enabled advertising cookies. */
function grantAllConsent() {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  function gtag() { window.dataLayer!.push(arguments); }
  // @ts-expect-error — gtag is untyped; called the same way as the inline <head> snippet
  gtag("consent", "update", {
    analytics_storage: "granted",
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
  });
}
